import type { Instance, CreateInstanceRequest, UpdateInstanceRequest } from '@/types/instance';

// Server facts (internal/api/handlers/instances.go, models/instance.go):
//  - name required (varchar 255), url required (varchar 500), token required on create;
//  - the 15-second server default applies to CREATE only (on update <= 0 keeps the current value);
//  - the server has NO minimum/maximum interval (<= 0 falls back to 15) and NO URL scheme
//    check, so the interval bounds and http/https rule below are client-side policy.
//  - update ignores empty name/url/token/interval (keeps current values).

export interface InstanceFormValues {
  name: string;
  url: string;
  portainerToken: string;
  syncIntervalSec: string;
  skipTlsVerify: boolean;
  isActive: boolean;
}

export type InstanceFormField = 'name' | 'url' | 'portainerToken' | 'syncIntervalSec';

export const DEFAULT_SYNC_INTERVAL_SEC = 300;
export const MIN_SYNC_INTERVAL_SEC = 60;
export const MAX_SYNC_INTERVAL_SEC = 86400;
export const MAX_NAME_LENGTH = 255;
export const MAX_URL_LENGTH = 500;

export function emptyInstanceForm(): InstanceFormValues {
  return {
    name: '',
    url: '',
    portainerToken: '',
    syncIntervalSec: String(DEFAULT_SYNC_INTERVAL_SEC),
    skipTlsVerify: false,
    isActive: true,
  };
}

export function instanceToForm(i: Instance): InstanceFormValues {
  return {
    name: i.name,
    url: i.url,
    portainerToken: '',
    syncIntervalSec: String(i.sync_interval_sec),
    skipTlsVerify: i.skip_tls_verify,
    isActive: i.is_active,
  };
}

function len(s: string): number {
  return Array.from(s).length;
}

export function validateInstanceForm(
  v: InstanceFormValues,
  mode: 'create' | 'edit',
  currentIntervalSec?: number,
): Partial<Record<InstanceFormField, string>> {
  const errors: Partial<Record<InstanceFormField, string>> = {};

  const name = v.name.trim();
  if (!name) errors.name = 'Enter a name.';
  else if (len(name) > MAX_NAME_LENGTH) errors.name = `Name must be ${MAX_NAME_LENGTH} characters or fewer.`;

  const url = v.url.trim();
  if (!url) {
    errors.url = 'Enter the Portainer URL.';
  } else {
    let parsed: URL | null;
    try {
      parsed = new URL(url);
    } catch {
      parsed = null;
    }
    if (!parsed) errors.url = 'Enter a valid URL, including http:// or https://.';
    else if (!/^https?:\/\//i.test(url)) errors.url = 'The URL must start with http:// or https://.';
    else if (parsed.username || parsed.password) errors.url = "Don't include a username or password in the URL.";
    else if (len(url) > MAX_URL_LENGTH) errors.url = `URL must be ${MAX_URL_LENGTH} characters or fewer.`;
  }

  if (mode === 'create' && !v.portainerToken.trim()) {
    errors.portainerToken = 'Enter the Portainer access token.';
  }

  const iv = v.syncIntervalSec.trim();
  if (!iv) errors.syncIntervalSec = 'Enter a number of seconds.';
  else if (!/^\d+$/.test(iv)) errors.syncIntervalSec = 'Enter a whole number of seconds.';
  else {
    const n = Number(iv);
    const unchanged = mode === 'edit' && currentIntervalSec !== undefined && n === currentIntervalSec;
    if (n < MIN_SYNC_INTERVAL_SEC && !unchanged)
      errors.syncIntervalSec = `The interval must be at least ${MIN_SYNC_INTERVAL_SEC} seconds.`;
    else if (n > MAX_SYNC_INTERVAL_SEC)
      errors.syncIntervalSec = `The interval must be at most ${MAX_SYNC_INTERVAL_SEC} seconds (24 hours).`;
  }

  return errors;
}

// Call only after validateInstanceForm returned no errors.
export function toCreateRequest(v: InstanceFormValues): CreateInstanceRequest {
  return {
    name: v.name.trim(),
    url: v.url.trim(),
    portainer_token: v.portainerToken.trim(),
    sync_interval_sec: Number(v.syncIntervalSec.trim()),
    skip_tls_verify: v.skipTlsVerify,
  };
}

export function toUpdateRequest(v: InstanceFormValues): UpdateInstanceRequest {
  const req: UpdateInstanceRequest = {
    name: v.name.trim(),
    url: v.url.trim(),
    sync_interval_sec: Number(v.syncIntervalSec.trim()),
    skip_tls_verify: v.skipTlsVerify,
    is_active: v.isActive,
  };
  const token = v.portainerToken.trim();
  if (token) req.portainer_token = token;
  return req;
}

type SyncFields = Pick<Instance, 'is_active' | 'last_sync_status'>;

export function instanceStatusKey(i: SyncFields): 'inactive' | 'failed' | 'in_progress' | 'success' | 'never' {
  if (!i.is_active) return 'inactive';
  if (i.last_sync_status === 'failed') return 'failed';
  if (i.last_sync_status === 'in_progress') return 'in_progress';
  if (i.last_sync_status === 'success') return 'success';
  return 'never';
}

export function syncHealth(i: SyncFields): 'inactive' | 'failed' | 'syncing' | 'ok' | 'never' {
  const k = instanceStatusKey(i);
  if (k === 'in_progress') return 'syncing';
  if (k === 'success') return 'ok';
  return k;
}

// Mirrors InstanceHandler.Delete: stops sync/resource workers, then in one transaction
// soft-deletes endpoints, containers, stacks, volumes, networks and the instance row.
// It never calls Portainer and does not touch apps, deployments or stats.
export function deleteImpactText(i: Instance): string {
  return (
    `Delete "${i.name}"? This stops syncing it and removes the instance and its synced endpoints, containers, stacks, volumes and networks from the Bridge. ` +
    'Nothing is deleted in Portainer itself, so running containers and stacks keep running. ' +
    'Apps and deployment history recorded in the Bridge are kept but will point at endpoints that no longer exist.'
  );
}
